import {
  AuthScreen,
  PasswordRecoveryForm,
  PasswordResetLinkForm,
  type PasswordResetLinkScreenState,
} from '@h2hspark/auth-ui';
import {
  PASSWORD_HINT,
  PASSWORD_RESET_SIGNED_IN_NOTICE,
  describePasswordViolation,
  recoveryAddressFromFragment,
} from '@h2hspark/contracts';
import { toast } from '@h2hspark/ui';
import * as React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthBrandMark } from '../auth-brand-mark';

/**
 * What the page says above the card, for each thing the link step can be showing.
 *
 * A dead link gets its own heading. It used to keep *"Choose a new password"* over a card that
 * said the link no longer worked and offered no field — the largest text on the screen
 * contradicting the only sentence in it.
 */
const LINK_HEADINGS: Record<
  PasswordResetLinkScreenState,
  { readonly title: string; readonly description: string }
> = {
  checking: {
    title: 'Choose a new password',
    description: 'Checking the link you followed.',
  },
  ready: {
    title: 'Choose a new password',
    description: 'Enter a new password for your H2HSpark account.',
  },
  dead: {
    title: 'This link no longer works',
    description: 'Reset links last one hour and can be used once.',
  },
  done: {
    title: 'Your password is set',
    description: 'Sign in with the new one to continue.',
  },
};

/**
 * Password recovery. Two ways in, one screen.
 *
 * **Without a token** — the ordinary case — the form emails a code, takes it back with a new
 * password, and the API answers with a fresh session, so a success lands the member straight in
 * the app (`USR-004` AC-1), with a notice that says so (AC-9).
 *
 * **With `?token=`** the member arrived from the link the platform mails when their address was
 * never verified, which Cognito's own recovery refuses to send a code to (`USR-004` AC-8). That
 * link confirms the address and sets the new password in one step; the form inspects it first, so
 * a used or expired one explains itself with no password field on screen (AC-2), and a success
 * ends at sign-in rather than a session, because the change signed the account out everywhere.
 *
 * ## The chrome, 2026-09-12
 *
 * `AuthScreen chrome="brand"` — the sign-in screen's own ground, mark, heading and card. This was
 * `bg-muted/20` behind a plain `Card`, which is where the member who has just been shown the
 * revised sign-in arrives when they press *Forgot?*. `USR-004` AC-1 makes the two one journey in
 * as many words — *"requests a reset **from the sign-in screen**"* — and it was two designs.
 *
 * Both states keep their own words and their own behaviour; only the chrome around them changed.
 */
export function MemberRecoveryPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  // The reset mail links back here as `#email=<address>` (TW 44131947), so the person lands on the
  // code step with their address filled in and types only the code. A fragment, so the address
  // never reaches a server log — see `RECOVERY_ADDRESS_FRAGMENT_PARAM`.
  const codeAlreadySentTo = recoveryAddressFromFragment(useLocation().hash);
  const [linkState, setLinkState] = React.useState<PasswordResetLinkScreenState>('checking');
  const heading = LINK_HEADINGS[linkState];

  return (
    <AuthScreen
      chrome="brand"
      mark={<AuthBrandMark />}
      title={token === null ? 'Recover access' : heading.title}
      description={
        token === null
          ? "We'll email you a recovery code so you can choose a new password."
          : heading.description
      }
      footer={
        <button type="button" className="auth-ui-link" onClick={() => navigate('/app/login')}>
          Back to sign in
        </button>
      }
    >
      {token === null ? (
        <PasswordRecoveryForm
          onSuccess={() => {
            // USR-004 AC-9: the code route signs the person in, so say the reset worked.
            toast.success(PASSWORD_RESET_SIGNED_IN_NOTICE);
            navigate('/app', { replace: true });
          }}
          // TW 44131867: the sign-up's rule and hint, checked before anything is sent.
          describePasswordViolation={describePasswordViolation}
          passwordHint={PASSWORD_HINT}
          recoveryScreen="member"
          {...(codeAlreadySentTo !== undefined ? { codeAlreadySentTo } : {})}
        />
      ) : (
        <PasswordResetLinkForm
          token={token}
          describePasswordViolation={describePasswordViolation}
          onSignIn={() => navigate('/app/login', { replace: true })}
          onRequestAnother={() => navigate('/app/recovery', { replace: true })}
          onScreenStateChange={setLinkState}
        />
      )}
    </AuthScreen>
  );
}
